import React, { useState } from 'react';
import { Button, Dialog, DialogContent, IconButton, Stack, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';

const EXTERNAL_APP_URL =
  'https://digitalmortgage.candidcrm.com/Jet-HomeLoans/005fI000000hPWD&00DfI000004W7Ue';

/**
 * Reusable Apply Now flow button.
 * Replicates the same two-step modal experience as the navbar's APPLY NOW button.
 *
 * Step 1: "Are you currently working with one of our Loan Officers?"
 *   YES -> /find-a-loan-officer
 *   NO  -> Step 2
 * Step 2: "No Problem! Get Started Today" -> opens EXTERNAL_APP_URL in new tab
 */
export default function ApplyFlowButton({ label = 'APPLY NOW', sx = {}, fullWidth = false, size = 'large' }) {
  const navigate = useNavigate();
  const [openAsk, setOpenAsk] = useState(false);
  const [openNoFlow, setOpenNoFlow] = useState(false);

  const startApplyFlow = () => setOpenAsk(true);

  const handleAskYes = () => {
    setOpenAsk(false);
    navigate('/find-a-loan-officer');
  };

  const handleAskNo = () => {
    setOpenAsk(false);
    setOpenNoFlow(true);
  };

  const handleApplyExternal = () => {
    window.open(EXTERNAL_APP_URL, '_blank', 'noopener,noreferrer');
    setOpenNoFlow(false);
  };

  return (
    <>
      <Button
        variant="contained"
        size={size}
        fullWidth={fullWidth}
        onClick={startApplyFlow}
        sx={{
          bgcolor: '#b89b5e',
          color: '#fff',
          fontWeight: 700,
          textTransform: 'uppercase',
          borderRadius: 1,
          px: 3,
          py: 0.9375,
          letterSpacing: 0.375,
          '&:hover': { bgcolor: '#ab9767' },
          ...sx,
        }}
      >
        {label}
      </Button>

      {/* Step 1 modal */}
      <Dialog
        open={openAsk}
        onClose={() => setOpenAsk(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 2, p: { xs: 2, md: 3 } } }}
      >
        <IconButton
          onClick={() => setOpenAsk(false)}
          sx={{ position: 'absolute', right: 8, top: 8, color: 'text.secondary' }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent>
          <Stack spacing={3} textAlign="center">
            <Typography variant="h4" sx={{ fontWeight: 800 }}>
              Are you currently working with one of our Loan Officers?
            </Typography>
            <Typography color="text.secondary">
              This will save you time by connecting you with the right person.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
              <Button
                variant="contained"
                onClick={handleAskYes}
                sx={{ bgcolor: '#b79a59', '&:hover': { bgcolor: '#a88a4d' }, px: 5 }}
              >
                YES
              </Button>
              <Button
                variant="contained"
                onClick={handleAskNo}
                sx={{ bgcolor: '#b79a59', '&:hover': { bgcolor: '#a88a4d' }, px: 5 }}
              >
                NO
              </Button>
            </Stack>
          </Stack>
        </DialogContent>
      </Dialog>

      {/* Step 2 modal */}
      <Dialog
        open={openNoFlow}
        onClose={() => setOpenNoFlow(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 2, p: { xs: 2, md: 3 } } }}
      >
        <IconButton
          onClick={() => setOpenNoFlow(false)}
          sx={{ position: 'absolute', right: 8, top: 8, color: 'text.secondary' }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent>
          <Stack spacing={3} textAlign="center">
            <Typography variant="h4" sx={{ fontWeight: 800 }}>
              No Problem! Get Started Today.
            </Typography>
            <Typography color="text.secondary">
              Start your application and we&rsquo;ll connect you with the right person.
            </Typography>
            <Button
              variant="contained"
              onClick={handleApplyExternal}
              sx={{ bgcolor: '#b79a59', '&:hover': { bgcolor: '#a88a4d' }, px: 5, alignSelf: 'center' }}
            >
              CLICK HERE TO APPLY
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  );
}
